import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CourseGrid from './CourseGrid';
import CourseAdminDialog from './CourseAdminDialog';
import LessonGrid from './LessonGrid';
import LessonAdminDialog from './LessonAdminDialog';
import LessonPlayer, { getLessonPlayerSnapshotKey } from './LessonPlayer';
import { CourseApi, TrainingApi, SpeedReadingCourse, SpeedReadingLesson } from './trainingApi';
import { useLessonStatus } from './useLessonEvents';
import checkPermission from '../../../utils/permission';

const isSuperAdminUser = (): boolean => {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem('userData') : null;
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return Boolean(parsed?.isSuperAdmin);
  } catch {
    return false;
  }
};

const SELECTED_LESSON_STORAGE_KEY = 'speed-reading-training-selected-lesson-id';
const SELECTED_COURSE_STORAGE_KEY = 'speed-reading-training-selected-course-id';

const readSession = (key: string) => {
  try { return typeof window !== 'undefined' ? window.sessionStorage.getItem(key) : null; } catch { return null; }
};
const writeSession = (key: string, value: string | null) => {
  if (typeof window === 'undefined') return;
  if (value) window.sessionStorage.setItem(key, value);
  else window.sessionStorage.removeItem(key);
};

const SpeedReadingTrainingPanel: React.FC = () => {
  const canManage = useMemo(() => isSuperAdminUser() || checkPermission(['speed_reading_lesson_add', 'speed_reading_lesson_update']), []);
  const canDelete = useMemo(() => isSuperAdminUser() || checkPermission('speed_reading_lesson_delete'), []);

  // Course list
  const [courses, setCourses] = useState<SpeedReadingCourse[]>([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [coursesError, setCoursesError] = useState<string | null>(null);
  const [courseAdminDialog, setCourseAdminDialog] = useState<{ open: boolean; course: SpeedReadingCourse | null }>({ open: false, course: null });

  // Selected course & its lessons
  const [selectedCourse, setSelectedCourse] = useState<SpeedReadingCourse | null>(null);
  const [lessons, setLessons] = useState<SpeedReadingLesson[]>([]);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [lessonsError, setLessonsError] = useState<string | null>(null);
  const [lessonAdminDialog, setLessonAdminDialog] = useState<{ open: boolean; lesson: SpeedReadingLesson | null }>({ open: false, lesson: null });

  // Selected lesson (player)
  const [selectedLesson, setSelectedLesson] = useState<SpeedReadingLesson | null>(null);

  // Lesson processing tracker
  const [trackedLessonId, setTrackedLessonId] = useState<string | null>(null);
  const trackingEnabled = Boolean(trackedLessonId);
  const trackedEvent = useLessonStatus(trackedLessonId, trackingEnabled);

  const restoredRef = useRef(false);

  // Load courses
  const reloadCourses = useCallback(async () => {
    try {
      setCoursesError(null);
      const data = await CourseApi.list({ includeAll: canManage });
      setCourses(data);
    } catch (err: any) {
      setCoursesError(err?.message || 'Kurslar yüklenemedi');
    } finally {
      setCoursesLoading(false);
    }
  }, [canManage]);

  useEffect(() => { void reloadCourses(); }, [reloadCourses]);

  // Load lessons for selected course
  const reloadLessons = useCallback(async (courseId: string) => {
    setLessonsLoading(true);
    setLessonsError(null);
    try {
      const data = await TrainingApi.list({ includeAll: canManage, courseId } as any);
      setLessons(data);
    } catch (err: any) {
      setLessonsError(err?.message || 'Dersler yüklenemedi');
    } finally {
      setLessonsLoading(false);
    }
  }, [canManage]);

  // Lesson processing tracking
  useEffect(() => {
    if (!trackedEvent || !trackedLessonId) return;
    setLessons((prev) => prev.map((item) => (
      item.id === trackedLessonId
        ? {
            ...item,
            status: trackedEvent.status === 'NOT_FOUND' ? item.status : trackedEvent.status,
            processingError: trackedEvent.error ?? item.processingError,
            audioDurationMs: trackedEvent.audioDurationMs ?? item.audioDurationMs,
            audioMedia: trackedEvent.audioUrl
              ? { ...(item.audioMedia || { id: '', mimeType: 'audio/wav' }), url: trackedEvent.audioUrl }
              : item.audioMedia,
          }
        : item
    )));
    if (trackedEvent.status === 'READY' || trackedEvent.status === 'FAILED') {
      TrainingApi.get(trackedLessonId)
        .then((fresh) => setLessons((prev) => prev.map((item) => (item.id === fresh.id ? fresh : item))))
        .catch(() => selectedCourse && void reloadLessons(selectedCourse.id));
      setTrackedLessonId(null);
    }
  }, [trackedEvent, trackedLessonId, selectedCourse, reloadLessons]);

  useEffect(() => {
    if (trackedLessonId) return;
    const pending = lessons.find((l) => l.status === 'DRAFT' || l.status === 'PROCESSING');
    if (pending) setTrackedLessonId(pending.id);
  }, [lessons, trackedLessonId]);

  // Restore session state
  useEffect(() => {
    if (coursesLoading || restoredRef.current || courses.length === 0) return;
    restoredRef.current = true;
    const storedCourseId = readSession(SELECTED_COURSE_STORAGE_KEY);
    if (!storedCourseId) return;
    const storedCourse = courses.find((c) => c.id === storedCourseId);
    if (!storedCourse) { writeSession(SELECTED_COURSE_STORAGE_KEY, null); return; }
    setSelectedCourse(storedCourse);
    void reloadLessons(storedCourse.id);
  }, [courses, coursesLoading, reloadLessons]);

  useEffect(() => {
    if (!selectedCourse || lessonsLoading || lessons.length === 0) return;
    const storedLessonId = readSession(SELECTED_LESSON_STORAGE_KEY);
    if (!storedLessonId || selectedLesson) return;
    const storedLesson = lessons.find((l) => l.id === storedLessonId);
    if (!storedLesson || storedLesson.status !== 'READY') { writeSession(SELECTED_LESSON_STORAGE_KEY, null); return; }
    TrainingApi.get(storedLessonId).then(setSelectedLesson).catch(() => setSelectedLesson(storedLesson));
  }, [lessons, lessonsLoading, selectedCourse, selectedLesson]);

  // Handlers: courses
  const handleSelectCourse = (course: SpeedReadingCourse) => {
    setSelectedCourse(course);
    writeSession(SELECTED_COURSE_STORAGE_KEY, course.id);
    void reloadLessons(course.id);
  };

  const handleCourseSaved = (course: SpeedReadingCourse) => {
    setCourseAdminDialog({ open: false, course: null });
    setCourses((prev) => {
      const exists = prev.some((c) => c.id === course.id);
      if (exists) return prev.map((c) => (c.id === course.id ? course : c));
      return [course, ...prev];
    });
  };

  const handleDeleteCourse = async (course: SpeedReadingCourse) => {
    if (!canDelete) return;
    if (!window.confirm(`"${course.title}" ana dersini silmek istediğine emin misin? İçindeki dersler de silinebilir.`)) return;
    try {
      await CourseApi.remove(course.id);
      setCourses((prev) => prev.filter((c) => c.id !== course.id));
    } catch (err: any) {
      window.alert(err?.message || 'Silme başarısız');
    }
  };

  // Handlers: lessons
  const handleLessonSaved = (lesson: SpeedReadingLesson) => {
    setLessonAdminDialog({ open: false, lesson: null });
    setLessons((prev) => {
      const exists = prev.some((item) => item.id === lesson.id);
      if (exists) return prev.map((item) => (item.id === lesson.id ? lesson : item));
      return [lesson, ...prev];
    });
    if (lesson.status === 'PROCESSING' || lesson.status === 'DRAFT') setTrackedLessonId(lesson.id);
    // Update lesson count in course list
    if (selectedCourse) {
      setCourses((prev) => prev.map((c) => c.id === selectedCourse.id
        ? { ...c, lessons: [...(c.lessons || []).filter((l) => l.id !== lesson.id), { id: lesson.id, title: lesson.title, status: lesson.status, audioDurationMs: lesson.audioDurationMs, order: lesson.order, isActive: lesson.isActive }] }
        : c
      ));
    }
  };

  const handleDeleteLesson = async (lesson: SpeedReadingLesson) => {
    if (!canDelete) return;
    if (!window.confirm(`"${lesson.title}" dersini silmek istediğine emin misin?`)) return;
    try {
      await TrainingApi.remove(lesson.id);
      setLessons((prev) => prev.filter((item) => item.id !== lesson.id));
    } catch (err: any) {
      window.alert(err?.message || 'Silme başarısız');
    }
  };

  const handleReprocessLesson = async (lesson: SpeedReadingLesson) => {
    try {
      const updated = await TrainingApi.reprocess(lesson.id);
      setLessons((prev) => prev.map((item) => (item.id === updated.id ? { ...updated, status: 'PROCESSING' } : item)));
      setTrackedLessonId(updated.id);
    } catch (err: any) {
      window.alert(err?.message || 'Yeniden işleme başlatılamadı');
    }
  };

  const handleSelectLesson = async (lesson: SpeedReadingLesson) => {
    if (lesson.status !== 'READY') return;
    writeSession(SELECTED_LESSON_STORAGE_KEY, lesson.id);
    try {
      const fresh = await TrainingApi.get(lesson.id);
      setSelectedLesson(fresh);
    } catch {
      setSelectedLesson(lesson);
    }
  };

  const handleBackFromLesson = () => {
    writeSession(SELECTED_LESSON_STORAGE_KEY, null);
    if (selectedLesson) {
      window.sessionStorage.removeItem(getLessonPlayerSnapshotKey(selectedLesson.id));
    }
    setSelectedLesson(null);
  };

  const handleReorderLessons = async (newOrder: SpeedReadingLesson[]) => {
    setLessons(newOrder);
    await Promise.allSettled(
      newOrder.map((lesson, index) => TrainingApi.update(lesson.id, { order: index } as any))
    );
  };

  const handleBackFromCourse = () => {
    setSelectedCourse(null);
    setLessons([]);
    writeSession(SELECTED_COURSE_STORAGE_KEY, null);
    writeSession(SELECTED_LESSON_STORAGE_KEY, null);
  };

  // ── Player ──────────────────────────────────────────────
  if (selectedLesson) {
    return <LessonPlayer lesson={selectedLesson} onBack={handleBackFromLesson} />;
  }

  // ── Course detail (lesson list) ──────────────────────────
  if (selectedCourse) {
    return (
      <div className="h-full overflow-y-auto bg-[#f2f6f8] p-4 sm:p-8">
        <div className="mx-auto max-w-[90rem]">

          {/* Breadcrumb */}
          <button
            type="button"
            onClick={handleBackFromCourse}
            className="mb-5 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold text-[#174e67] shadow-sm ring-1 ring-[#dbe7ec] transition hover:bg-[#eef6f8] hover:text-[#0ea5b7]"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
            Tüm kurslar
          </button>

          {/* Course Hero */}
          <div className="relative mb-8 overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#0a2e42] via-[#0e5f73] to-[#174e67] shadow-[0_20px_60px_rgba(11,31,41,0.22)]">
            {selectedCourse.coverMedia?.url ? (
              <img src={selectedCourse.coverMedia.url} alt={selectedCourse.title} className="absolute inset-0 h-full w-full object-cover opacity-20" />
            ) : null}
            <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-[#0ea5b7]/15 blur-3xl" />
            <div className="relative px-6 py-8 sm:px-10 sm:py-10">
              <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 ring-1 ring-white/15">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#0ea5b7]" />
                    <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#5dd8e8]">Hızlı Okuma · Ana Ders</p>
                  </div>
                  <h2 className="text-[1.65rem] font-black leading-tight tracking-tight text-white sm:text-[2.1rem]">
                    {selectedCourse.title}
                  </h2>
                  {selectedCourse.description ? (
                    <p className="mt-3 max-w-2xl text-sm leading-6 text-white/65">{selectedCourse.description}</p>
                  ) : null}
                  <div className="mt-5 flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2 rounded-2xl bg-white/10 px-4 py-2.5 ring-1 ring-white/10">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#5dd8e8]">
                        <path d="M3 11a9 9 0 1 1 18 0v3a9 9 0 1 1-18 0z"/>
                        <path d="M9 12a3 3 0 1 0 6 0 3 3 0 0 0-6 0Z"/>
                      </svg>
                      <span className="text-sm font-bold text-white/90">{lessons.length} ders</span>
                    </div>
                    {lessons.filter((l) => l.status === 'READY').length > 0 ? (
                      <div className="flex items-center gap-2 rounded-2xl bg-emerald-500/20 px-4 py-2.5 ring-1 ring-emerald-400/25">
                        <span className="h-2 w-2 rounded-full bg-emerald-400" />
                        <span className="text-sm font-bold text-emerald-200">{lessons.filter((l) => l.status === 'READY').length} hazır</span>
                      </div>
                    ) : null}
                  </div>
                </div>
                {canManage ? (
                  <button
                    type="button"
                    onClick={() => setLessonAdminDialog({ open: true, lesson: null })}
                    className="self-start rounded-2xl bg-white px-5 py-3 text-sm font-black text-[#174e67] shadow-[0_10px_30px_rgba(0,0,0,0.15)] transition hover:bg-white/90 active:scale-95 lg:self-center"
                  >
                    + Yeni Ders
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          {lessonsError ? (
            <div className="mb-6 rounded-2xl bg-[#fef4f4] px-4 py-3 text-sm font-semibold text-[#a04c4c]">{lessonsError}</div>
          ) : null}

          {lessonsLoading ? (
            <div className="flex items-center justify-center py-24">
              <div className="flex flex-col items-center gap-4">
                <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-[#0ea5b7] border-t-transparent" />
                <p className="text-sm font-semibold text-[#67808c]">Dersler yükleniyor…</p>
              </div>
            </div>
          ) : (
            <LessonGrid
              lessons={lessons}
              canManage={canManage}
              onSelect={handleSelectLesson}
              onEdit={(lesson) => setLessonAdminDialog({ open: true, lesson })}
              onDelete={canDelete ? handleDeleteLesson : undefined}
              onReprocess={canManage ? handleReprocessLesson : undefined}
              onReorder={canManage ? handleReorderLessons : undefined}
            />
          )}
        </div>

        {lessonAdminDialog.open ? (
          <LessonAdminDialog
            initialLesson={lessonAdminDialog.lesson}
            courseId={selectedCourse.id}
            onClose={() => setLessonAdminDialog({ open: false, lesson: null })}
            onSaved={handleLessonSaved}
          />
        ) : null}
      </div>
    );
  }

  // ── Course list (home) ───────────────────────────────────
  return (
    <div className="h-full overflow-y-auto bg-[#f2f6f8] p-4 sm:p-8">
      <div className="mx-auto max-w-[90rem]">

        <div className="relative mb-8 overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#0a2e42] via-[#0e5f73] to-[#174e67] px-6 py-8 shadow-[0_20px_60px_rgba(11,31,41,0.22)] sm:px-10 sm:py-10">
          <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-[#0ea5b7]/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-12 -left-12 h-48 w-48 rounded-full bg-[#174e67]/40 blur-2xl" />

          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 ring-1 ring-white/15 backdrop-blur-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-[#0ea5b7]" />
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#5dd8e8]">Hızlı Okuma · Eğitim</p>
              </div>
              <h2 className="text-[1.65rem] font-black leading-tight tracking-tight text-white sm:text-[2.1rem]">
                Eğitim Kursları
              </h2>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-white/65">
                Her kurs kendi içinde sıralı dersler barındırır. Bir kursa tıklayarak içindeki derslere ulaş,
                eğitmen sesiyle etkileşimli olarak çalış.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 rounded-2xl bg-white/10 px-4 py-2.5 ring-1 ring-white/10">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#5dd8e8]">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                  </svg>
                  <span className="text-sm font-bold text-white/90">{courses.length} kurs</span>
                </div>
                <div className="flex items-center gap-2 rounded-2xl bg-white/10 px-4 py-2.5 ring-1 ring-white/10">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#5dd8e8]">
                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                  </svg>
                  <span className="text-sm font-bold text-white/90">Yapay zeka destekli</span>
                </div>
              </div>
            </div>

            {canManage ? (
              <button
                type="button"
                onClick={() => setCourseAdminDialog({ open: true, course: null })}
                className="self-start rounded-2xl bg-white px-5 py-3 text-sm font-black text-[#174e67] shadow-[0_10px_30px_rgba(0,0,0,0.15)] transition hover:bg-white/90 active:scale-95 lg:self-center"
              >
                + Yeni Ana Ders
              </button>
            ) : null}
          </div>
        </div>

        {coursesError ? (
          <div className="mb-6 rounded-2xl bg-[#fef4f4] px-4 py-3 text-sm font-semibold text-[#a04c4c]">{coursesError}</div>
        ) : null}

        {coursesLoading ? (
          <div className="flex items-center justify-center py-24">
            <div className="flex flex-col items-center gap-4">
              <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-[#0ea5b7] border-t-transparent" />
              <p className="text-sm font-semibold text-[#67808c]">Kurslar yükleniyor…</p>
            </div>
          </div>
        ) : (
          <CourseGrid
            courses={courses}
            canManage={canManage}
            onSelect={handleSelectCourse}
            onEdit={(course) => setCourseAdminDialog({ open: true, course })}
            onDelete={canDelete ? handleDeleteCourse : undefined}
          />
        )}
      </div>

      {courseAdminDialog.open ? (
        <CourseAdminDialog
          initialCourse={courseAdminDialog.course}
          onClose={() => setCourseAdminDialog({ open: false, course: null })}
          onSaved={handleCourseSaved}
        />
      ) : null}
    </div>
  );
};

export default SpeedReadingTrainingPanel;
